import * as React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { Card, Grid } from "@mantine/core";
import { bindActionCreators } from "redux";
import * as PostActions from "../../data/actions/postActions";
import { IState } from "../../data/reducers/state";
import { Role } from "../../types/persons/Role";
import { IPerson } from "../../types/persons/person";
import { Privilege } from "../../types/persons/privilege";
import { IPost } from "../../types/post/Post";
import ActionableAppointments from "../appointments/actionable/ActionableAppointments";
import UpcomingAppointments from "./UpcomingAppointments";
import UpcomingShifts from "./UpcomingShifts";

interface IHomePageProps {
  self: IPerson;
  role: Role;
  privilege: Privilege;
  posts: IPost[];
  actions: {
    getPosts: () => Promise<IPost[]>;
  };

  config: {
    usesAvailabilities: boolean;
  };
}

const HomePage: React.FunctionComponent<IHomePageProps> = (props) => {
  React.useEffect(() => {
    document.title = "Stadium Supervisor - Home";
    props.actions.getPosts();
  }, []);

  const { self, posts } = props;

  const { role, privilege } = props;
  const actions: [string, string][] = [];

  const isReferee = Role.hasRole(role, Role.referee);
  const isManager = Privilege.canManage(privilege);
  const seeRosterPage = isManager || Role.hasAnyRole(role, [Role.supervisor, Role.administrative]);

  const extraComponents: any[] = [];

  // eventually you could insert actions that have to be done
  if (seeRosterPage) {
    actions.push(["Roster", "/roster"]);
  }

  if (isManager) {
    extraComponents.push(
      <ErrorBoundary
        key="actionable-appointments"
        name="actionable appointments"
      >
        <ActionableAppointments key="actionable" />
      </ErrorBoundary>,
    );
  }

  if (isReferee && props.config.usesAvailabilities) {
    actions.push([
      "Availability",
      `/u/${self.externalIdentifier}/availabilities`,
    ]);
  }
  if (isManager) actions.push(["Shift manager", "/admin/shifts"]);

  return (
    <div style={{ maxWidth: "968px", margin: "0px auto", padding: "0 16px" }}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          {self && (
            <p style={{ marginBottom: "4px" }} className="grey">
              Hi {self.preferredName || self.firstName},
            </p>
          )}
          <h3>Welcome to Stadium Supervisor!</h3>
          <ul style={{ fontSize: "18px" }}>
            {actions.map((a, idx) => (
              <li key={`actions-${a[1]}`}>
                <Link to={a[1]}>{a[0]}</Link>
              </li>
            ))}
          </ul>
          <UpcomingShifts />

          {/* <CreateAvailabilityForm person="" /> */}
          {extraComponents}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <UpcomingAppointments />
        </Grid.Col>
      </Grid>
    </div>
  );
};

function mapStateToProps(state: IState) {
  const self = state.persons.map[state.persons.self];
  return {
    posts: state.posts,
    self,
    role: state.auth.role,
    privilege: state.auth.privilege,
    config: {
      usesAvailabilities: self?.associations?.some(
        (a) =>
          state.associations?.map[a?.associationId]?.config?.availabilities
            ?.enabled,
      ),
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(PostActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(HomePage);

class ErrorBoundary extends React.PureComponent<
  { children: any; name: string },
  { componentError?: string }
> {
  constructor(props) {
    super(props);
    this.state = { componentError: undefined };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { componentError: error.toString() };
  }

  render() {
    if (this.state.componentError) {
      // You can render any custom fallback UI
      return (
        <Card padding="md">
          <p>
            An error occurred when displaying {this.props.name}:{" "}
            {this.state.componentError}
          </p>
        </Card>
      );
    }

    return this.props.children;
  }
}
